'use client'

import React, {useCallback, useEffect, useId, useRef} from 'react'
import Link from 'next/link'
import {useDialogFocusTrap, isTopmostModal, markModalEscapeHandled, MODAL_ESCAPE_HANDLED, type ModalKeyboardEvent} from '@fitro/modal-routing'
import {cn} from '../../../lib/utils'
import {BottomSheet} from '@fitro/customer-ui/BottomSheet'
import {usePortalContainer} from '../../../lib/portal-root'
import {useScrollLock} from '../../../hooks/useScrollLock'
import {
    isPenPointer,
    suppressNextCompatibleClick,
} from '../../../lib/pointer-events'
import {CUSTOMER_OVERLAY_LAYERS} from '../../../lib/overlay-layers'
import {X} from 'lucide-react'

interface ActionItemProps {
    label: string
    icon?: React.ComponentType<{ className?: string }>
    onClick: () => void
    href?: string
    variant?: 'default' | 'primary' | 'destructive'
    badge?: string | number
    disabled?: boolean
    isActive?: boolean
}

type ActionSheetProps = {
    open: boolean
    onClose: () => void
    title?: string
    description?: string
    children: React.ReactNode
    footer?: React.ReactNode
    /** Left-aligned header with a close button carrying this label. */
    closeLabel?: string
    className?: string
    contentClassName?: string
    contentMaxHeightClassName?: string
    contentScrollable?: boolean
    panelId?: string
    backdropZIndex?: number
    panelZIndex?: number
    selectionBoundary?: boolean
    returnFocusRef?: React.RefObject<HTMLElement | null>
} & (
    | {showCancel?: true; cancelText: string}
    | {showCancel: false; cancelText?: never}
)

const ActionSheetContext = React.createContext<(() => void) | null>(null)

export const ActionSheet: React.FC<ActionSheetProps> = ({
                                                            open,
                                                            onClose,
                                                            title,
                                                            description,
                                                            children,
                                                            footer,
                                                            closeLabel,
                                                            showCancel = true,
                                                            cancelText,
                                                            className,
                                                            contentClassName,
                                                            contentMaxHeightClassName,
                                                            contentScrollable = true,
                                                            panelId,
                                                            backdropZIndex = CUSTOMER_OVERLAY_LAYERS.actionSheetBackdrop,
                                                            panelZIndex = CUSTOMER_OVERLAY_LAYERS.fullscreenEditor,
                                                            selectionBoundary = false,
                                                            returnFocusRef,
                                                        }) => {
    const sheetRef = useRef<HTMLDivElement>(null)
    const previouslyFocusedRef = useRef<HTMLElement | null>(null)
    const titleId = useId()
    const portalContainer = usePortalContainer()

    useScrollLock({
        enabled: open,
    })
    useDialogFocusTrap(open, sheetRef, {tabNavigation: selectionBoundary ? 'all-controls' : 'native'})

    const setSheetRef = useCallback((node: HTMLDivElement | null) => {
        const previousSheet = sheetRef.current
        sheetRef.current = node
        if (node) {
            previouslyFocusedRef.current ??= returnFocusRef?.current ?? (document.activeElement as HTMLElement | null)
            node.focus({preventScroll: true})
            return
        }
        if (!previousSheet) return

        // Ref cleanup also runs during StrictMode replay. Wait for the commit
        // to distinguish actual removal after exit from a reattached panel.
        queueMicrotask(() => {
            if (previousSheet.isConnected || sheetRef.current) return

            // Compatibility mouse focus may land in the exiting panel after dismissal.
            const active = document.activeElement
            const shouldRestore = active === document.body || previousSheet.contains(active)
            if (shouldRestore && previouslyFocusedRef.current?.isConnected) {
                previouslyFocusedRef.current.focus({preventScroll: true})
            }
            previouslyFocusedRef.current = null
        })
    }, [returnFocusRef])

    useEffect(() => {
        if (!open) return

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !(event as ModalKeyboardEvent)[MODAL_ESCAPE_HANDLED]
                && isTopmostModal(sheetRef.current) && markModalEscapeHandled(event)) {
                event.preventDefault()
                onClose()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [open, onClose])

    if (typeof window === 'undefined' || !portalContainer) return null

    const handleCloseClick = () => {
        onClose()
    }

    const handlePenClosePointerUp = (event: React.PointerEvent<HTMLElement>) => {
        if (!isPenPointer(event)) return

        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        onClose()
    }

    return (
        <BottomSheet
            open={open}
            onClose={onClose}
            container={portalContainer}
            panelRef={setSheetRef}
            id={panelId}
            data-fitro-selection-overlay={selectionBoundary ? 'true' : undefined}
            data-modal-root
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            className={className}
            panelZIndex={panelZIndex}
            backdropZIndex={backdropZIndex}
            onBackdropClick={handleCloseClick}
            onBackdropPointerUp={handlePenClosePointerUp}
        >
            {closeLabel ? (
                <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3">
                    <div className="min-w-0">
                        {title ? (
                            <h3 id={titleId} className="break-words text-lg font-semibold leading-7 text-gray-900 dark:text-white">{title}</h3>
                        ) : null}
                        {description ? (
                            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{description}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={handleCloseClick}
                        onPointerUp={handlePenClosePointerUp}
                        className="-m-2 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full p-2 text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label={closeLabel}
                    >
                        <X className="h-5 w-5" aria-hidden="true"/>
                    </button>
                </div>
            ) : (title || description) && (
                <div className="shrink-0 px-4 pb-3 text-center">
                    {title && (
                        <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
                    )}
                    {description && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {description}
                        </p>
                    )}
                </div>
            )}


            <div
                className={cn(
                    'min-h-0 flex-1 overscroll-contain',
                    contentScrollable ? 'ios-momentum-scroll overflow-y-auto' : 'overflow-hidden',
                    contentMaxHeightClassName,
                    'px-4',
                    contentClassName
                )}
            >
                <ActionSheetContext.Provider value={onClose}>
                    {children}
                </ActionSheetContext.Provider>
            </div>


            {footer ? (
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] pt-4">
                    {footer}
                </div>
            ) : null}

            {showCancel && (
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] pt-4">
                    <button
                        type="button"
                        onClick={handleCloseClick}
                        onPointerUp={handlePenClosePointerUp}
                        className={cn(
                            'w-full py-3 px-4',
                            'bg-gray-100 dark:bg-gray-800',
                            'text-gray-900 dark:text-white',
                            'font-medium rounded-lg',
                            'hover:bg-gray-200 dark:hover:bg-gray-700',
                            'transition-colors duration-200',
                            'focus-visible:ring-2 focus-visible:ring-gray-400'
                        )}
                    >
                        {cancelText}
                    </button>
                </div>
            )}
        </BottomSheet>
    )
}

export const ActionItem: React.FC<ActionItemProps & { className?: string }> = ({
                                                                                   label,
                                                                                   icon: Icon,
                                                                                   onClick,
                                                                                   href,
                                                                                   variant = 'default',
                                                                                   badge,
                                                                                   disabled = false,
                                                                                   isActive = false,
                                                                                   className
                                                                               }) => {
    const closeSheet = React.useContext(ActionSheetContext)
    const variantClasses = {
        default: 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800',
        primary: 'text-primary hover:bg-primary/10',
        destructive: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
    }

    const content = (
        <>
            {Icon && (
                <Icon className="w-5 h-5 shrink-0"/>
            )}
            <span className={cn('flex-1 text-left font-medium', isActive && 'font-semibold')}>
        {label}
      </span>
            {badge && (
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          {badge}
        </span>
            )}
        </>
    )

    const itemClasses = cn(
        'w-full flex items-center gap-3 py-3 px-2',
        'rounded-lg transition-colors duration-200',
        'focus-visible:ring-2 focus-visible:ring-primary/50',
        'min-h-[56px]',
        variantClasses[variant],
        isActive && 'bg-primary-50 text-primary-900 border border-primary-100 shadow-sm hover:bg-primary-100 dark:bg-primary-900/40 dark:text-primary-100 dark:border-primary-800/40 dark:hover:bg-primary-900/60',
        disabled && 'opacity-50 cursor-not-allowed',
        className
    )

    if (href) {
        return (
            <Link
                href={href}
                onClick={() => {
                    if (disabled) return
                    closeSheet?.()
                }}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={disabled || undefined}
                tabIndex={disabled ? -1 : undefined}
                className={cn(itemClasses, disabled && 'pointer-events-none')}
            >
                {content}
            </Link>
        )
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-current={isActive ? 'page' : undefined}
            className={itemClasses}
        >
            {content}
        </button>
    )
}

ActionSheet.displayName = 'ActionSheet'
ActionItem.displayName = 'ActionItem'
